<template>
  <div style="padding-top: 16px" id="page-container">
    <div class="page-section jobs_container">
      <div :class="containerClass">
        <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-16pt" style="white-space: nowrap">
          <h2 class="mb-0">
            Bulk SMS
            <i
              class="material-icons icon-16pt clickable-item"
              v-b-popover.hover.top="`Send bulk SMS campaigns and manage student SMS conversations.`"
              >info</i
            >
          </h2>
          <div class="w-auto ml-sm-auto mb-sm-0">
            <b-btn @click.prevent="openComposeModal()" variant="primary">
              <i class="material-icons icon-16pt mr-1">add</i>Compose SMS
            </b-btn>
          </div>
        </div>

        <b-tabs v-model="activeTab" class="mb-0" @input="onTabChange">
          <b-tab>
            <template #title>
              <i class="material-icons icon-16pt mr-1" style="vertical-align: middle">send</i> Sent
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <i class="material-icons icon-16pt mr-1" style="vertical-align: middle">sms</i>
              SMS Inbox
              <b-badge v-if="inboxUnreadCount > 0" variant="danger" pill class="ml-1" style="font-size: 0.65rem">
                {{ inboxUnreadCount }}
              </b-badge>
            </template>
          </b-tab>
        </b-tabs>

        <div v-if="activeTab === 0">
          <div class="d-flex flex-wrap mt-16pt mb-16pt" style="gap: 8px">
            <div style="min-width: 200px; flex: 1">
              <b-form-input v-model="searchTerm" placeholder="Search messages..." @input="onSearch" />
            </div>
            <div style="min-width: 160px">
              <b-form-select v-model="filters.status" :options="statusOptions" @change="onFilterApplied" />
            </div>
            <div style="min-width: 180px">
              <v-select
                class="form-control v-select-custom"
                label="title"
                v-model="filters.program"
                :reduce="p => p.id"
                placeholder="All Programs"
                :options="allPrograms"
                :loading="areProgramsLoading"
                @input="onFilterApplied"
                :clearable="true"
              />
            </div>
          </div>

          <page-separator
            :title="
              !isLoading
                ? `showing ${totalMessages ? `${pageFromCount}-${pageToCount}` : 0} of ${totalMessages} messages`
                : ''
            "
          />

          <div class="d-flex justify-content-center" v-if="!totalMessages && !isLoading">
            <div class="card card-block card-stretch card-height col-md-12">
              <div class="card-body text-center">
                <i class="fas fa-paper-plane fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
                <h4>No Messages Sent Yet</h4>
                <p class="text-muted">You haven't sent any bulk messages yet. Click "Compose SMS" to get started.</p>
                <b-btn @click.prevent="openComposeModal()" variant="primary" class="mt-2">
                  <i class="material-icons icon-16pt mr-1">send</i>Compose SMS
                </b-btn>
              </div>
            </div>
          </div>

          <b-skeleton-wrapper :loading="isLoading" v-else>
            <template #loading>
              <div class="row card-group-row mb-lg-12pt home-card">
                <div class="col-sm-12 card-group-row__col" v-for="item in [1, 2, 3]" :key="item">
                  <div class="card overlay--show card-lg overlay--primary-dodger-blue card-group-row__card">
                    <div class="card-header d-flex align-items-center">
                      <a href="#" @click.prevent class="card-title flex"><b-skeleton width="60%" /></a>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <b-skeleton width="100%" />
                      <b-skeleton width="70%" />
                      <b-skeleton width="40%" />
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="row card-group-row mb-lg-12pt home-card">
              <div class="col-sm-12 card-group-row__col" v-for="msg in messages" :key="msg.id">
                <div
                  class="card overlay--show card-lg overlay--primary-dodger-blue card-group-row__card clickable-item"
                  @click="openDeliveryLogDrawer(msg)"
                >
                  <div class="card-header d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px">
                    <div class="d-flex align-items-center flex-wrap" style="gap: 8px">
                      <span class="card-title mb-0" style="margin-right: 8px">{{ msg.subject || 'SMS Campaign' }}</span>
                      <span :class="sentStatusBadgeClass(msg)" style="text-transform: none; font-size: 0.75rem">
                        {{ sentStatusLabel(msg) }}
                      </span>
                      <span :class="statusBadgeClass(msg.status)" style="text-transform: none; font-size: 0.75rem">
                        {{ statusLabel(msg.status) }}
                      </span>
                    </div>
                    <div>
                      <b-btn
                        v-if="msg.send_type === 'sms'"
                        size="sm"
                        variant="outline-primary"
                        style="font-size: 0.72rem; padding: 2px 8px"
                        @click.stop="openInboxTab(msg.id)"
                      >
                        <i class="material-icons icon-16pt" style="vertical-align: middle">inbox</i> Open Inbox
                      </b-btn>
                    </div>
                  </div>

                  <div class="card-body d-flex flex-column">
                    <div class="mb-2 text-muted" style="font-size: 0.9rem">
                      {{ truncate(stripHtml(msg.message), { length: 200, separator: /,? +/ }) }}
                    </div>

                    <div class="card-button">
                      <ul>
                        <li>
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">{{ sendTypeIcon(msg.send_type) }}</span>
                            {{ sendTypeLabel(msg.send_type) }}
                          </a>
                        </li>
                        <li v-if="get(msg, 'program.title')">
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">school</span>
                            {{ get(msg, 'program.title') }}
                          </a>
                        </li>
                        <li v-else>
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">people</span>
                            All Students
                          </a>
                        </li>
                        <li>
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">schedule</span>
                            {{ formatDateTime(msg.created_at) }}
                          </a>
                        </li>
                        <li v-if="msg.status === 'sent' || msg.total_recipients > 0">
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">group</span>
                            {{ deliveryCountLabel(msg) }}
                          </a>
                        </li>
                        <li v-if="get(msg, 'sent_by_name')">
                          <a href="#" @click.prevent>
                            <span class="material-icons icon-16pt text-100">person</span>
                            {{ msg.sent_by_name }}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-skeleton-wrapper>

          <pagination
            v-if="totalMessages > messages.length"
            v-model="currentPage"
            :total-rows="totalMessages"
            :per-page="perPage"
            @change="onPageChange"
            aria-controls="bulk-messages-table"
          />
        </div>

        <div v-if="activeTab === 1">
          <div class="d-flex flex-wrap mt-16pt mb-16pt align-items-center" style="gap: 8px">
            <div style="min-width: 220px; flex: 1">
              <b-form-input
                v-model="inboxSearch"
                placeholder="Search by student, phone, or message..."
                @input="onInboxSearch"
              />
            </div>
            <b-form-checkbox v-model="inboxUnreadOnly" switch @change="onInboxFilterApplied">Unread only</b-form-checkbox>
            <b-btn variant="outline-secondary" size="sm" @click="refreshInboxData">
              <i class="material-icons icon-16pt" style="vertical-align: middle">refresh</i>
              Refresh
            </b-btn>
          </div>

          <page-separator
            :title="
              !isInboxLoading
                ? `showing ${totalConversations ? `${inboxPageFrom}-${inboxPageTo}` : 0} of ${totalConversations} conversations`
                : ''
            "
          />

          <div class="d-flex justify-content-center" v-if="!totalConversations && !isInboxLoading">
            <div class="card card-block card-stretch card-height col-md-12">
              <div class="card-body text-center">
                <i class="material-icons text-muted mt-4 mb-4" style="font-size: 3rem">sms</i>
                <h4>SMS Inbox is Empty</h4>
                <p class="text-muted">Incoming student conversations will appear here.</p>
              </div>
            </div>
          </div>

          <b-skeleton-wrapper :loading="isInboxLoading" v-else>
            <template #loading>
              <div v-for="i in [1, 2, 3, 4]" :key="i" class="card mb-2">
                <div class="card-body py-3 d-flex align-items-center" style="gap: 12px">
                  <b-skeleton type="avatar" />
                  <div class="flex">
                    <b-skeleton width="40%" />
                    <b-skeleton width="70%" />
                  </div>
                  <b-skeleton width="80px" />
                </div>
              </div>
            </template>

            <div
              v-for="conversation in conversations"
              :key="conversation.id"
              class="card mb-2 clickable-item"
              :style="!conversation.is_read ? 'border-left: 3px solid #0069d9;' : 'border-left: 3px solid transparent;'"
              @click="openConversationThread(conversation)"
            >
              <div class="card-body py-3 d-flex align-items-center flex-wrap" style="gap: 12px">
                <div
                  class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style="width: 40px; height: 40px; background: #e9ecef; font-weight: 600; font-size: 1rem"
                >
                  {{ replyInitial(conversation) }}
                </div>
                <div class="flex" style="min-width: 0">
                  <div class="d-flex align-items-center" style="gap: 6px">
                    <span :class="!conversation.is_read ? 'font-weight-bold' : ''">
                      {{ threadDisplayName(conversation) }}
                    </span>
                    <b-badge v-if="!conversation.is_read" variant="primary" style="font-size: 0.6rem">UNREAD</b-badge>
                    <b-badge v-if="conversation.reply_count" variant="light" style="font-size: 0.6rem"
                      >{{ conversation.reply_count }} msgs</b-badge
                    >
                    <b-badge v-if="conversation.is_unknown_sender" variant="secondary" style="font-size: 0.6rem"
                      >Unknown</b-badge
                    >
                  </div>
                  <div class="text-muted text-truncate" style="font-size: 0.85rem; max-width: 560px">
                    <span class="mr-2">{{ threadParticipantPhone(conversation) || 'No phone' }}</span>
                    <span>· {{ conversation.last_message_preview || 'No messages yet.' }}</span>
                  </div>
                </div>
                <div class="text-muted flex-shrink-0" style="font-size: 0.8rem; white-space: nowrap">
                  {{ formatDateTime(threadLastMessageAt(conversation) || conversation.created_at) }}
                </div>
              </div>
            </div>
          </b-skeleton-wrapper>

          <pagination
            v-if="totalConversations > conversations.length"
            v-model="inboxCurrentPage"
            :total-rows="totalConversations"
            :per-page="perPage"
            @change="onInboxPageChange"
          />
        </div>
      </div>
    </div>

    <b-modal
      id="compose-bulk-message-modal"
      size="xl"
      centered
      hide-footer
      hide-header
      @hidden="resetForm"
      body-class="p-0"
    >
      <b-form @submit.prevent="onSubmit" class="p-0">
        <div class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom" style="background: #f8f9fa">
          <h5 class="mb-0" style="font-weight: 500; font-size: 1rem; color: #202124">Compose Bulk SMS</h5>
          <b-btn
            variant="link"
            class="p-0 text-muted"
            @click="$bvModal.hide('compose-bulk-message-modal')"
            style="font-size: 1.5rem; line-height: 1"
          >
            <i class="material-icons">close</i>
          </b-btn>
        </div>

        <div class="px-4 py-3">
          <div class="mb-3">
            <div class="d-flex align-items-start pb-2 border-bottom" style="gap: 12px">
              <label class="text-muted mb-0 pt-1" style="min-width: 50px; font-size: 0.85rem; font-weight: 500">To</label>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center flex-wrap mb-2" style="gap: 12px">
                  <b-form-radio v-model="audienceType" value="all" name="audience_type" @change="onAudienceTypeChange">
                    <span style="font-size: 0.85rem">All Students</span>
                  </b-form-radio>
                  <b-form-radio
                    v-model="audienceType"
                    value="program"
                    name="audience_type"
                    @change="onAudienceTypeChange"
                  >
                    <span style="font-size: 0.85rem">Program Specific</span>
                  </b-form-radio>
                  <b-form-radio v-model="audienceType" value="manual" name="audience_type" @change="onAudienceTypeChange">
                    <span style="font-size: 0.85rem">Manual Numbers</span>
                  </b-form-radio>
                </div>

                <div v-if="audienceType === 'program'" class="mb-2" style="max-width: 360px">
                  <v-select
                    class="form-control v-select-custom"
                    label="title"
                    v-model="form.program"
                    :reduce="p => p.id"
                    placeholder="Select Program..."
                    :options="allPrograms"
                    :loading="areProgramsLoading"
                    :clearable="true"
                    @input="onProgramChange"
                    style="font-size: 0.85rem"
                  />
                </div>

                <div v-if="audienceType === 'manual'" class="mt-1">
                  <b-form-input
                    v-model.trim="manualNumberInput"
                    placeholder="Type student name or phone number"
                    @input="onManualNumberInput"
                    @keydown.enter.prevent="addManualRecipientsFromInput"
                  />
                  <small class="text-muted d-block mt-1" style="font-size: 0.78rem">
                    Search students by name/phone and click to add, or press Enter for direct number input.
                  </small>

                  <div v-if="dashboardStudents.length > 0" class="d-flex flex-wrap align-items-center mt-2" style="gap: 6px">
                    <span
                      v-for="student in visibleStudents"
                      :key="`manual-sms-${recipientKey(student)}`"
                      class="d-inline-flex align-items-center manual-recipient-chip"
                      :class="{ 'manual-recipient-chip--selected': isManualRecipientSelected(student) }"
                      @click="toggleManualRecipientFromStudent(student)"
                    >
                      <span
                        class="rounded-circle d-inline-flex align-items-center justify-content-center mr-1"
                        style="width: 20px; height: 20px; background: #e9ecef; font-size: 0.62rem; font-weight: 600; overflow: hidden"
                      >
                        <img
                          v-if="recipientAvatar(student)"
                          :src="recipientAvatar(student)"
                          :alt="recipientName(student)"
                          style="width: 100%; height: 100%; object-fit: cover"
                        />
                        <span v-else>{{ recipientInitial(student) }}</span>
                      </span>
                      <span class="d-inline-flex flex-column align-items-start" style="line-height: 1.15">
                        <span style="font-weight: 500">{{ recipientName(student) }}</span>
                        <span class="text-muted">
                          {{ recipientPhone(student) || 'No phone' }}
                        </span>
                      </span>
                      <i v-if="isManualRecipientSelected(student)" class="material-icons ml-1 manual-recipient-check">done</i>
                    </span>
                    <b-btn
                      v-if="dashboardStudents.length > 5"
                      variant="link"
                      size="sm"
                      class="p-0 text-primary"
                      @click="toggleShowAllStudents"
                      style="font-size: 0.7rem; text-decoration: none; font-weight: 500"
                    >
                      {{ showAllStudents ? 'See less' : `+${dashboardStudents.length - 5} more` }}
                    </b-btn>
                  </div>

                  <div v-else-if="isDashboardStudentsLoading" class="mt-2">
                    <b-spinner small class="mr-2" />
                    <span class="text-muted" style="font-size: 0.82rem">Loading students...</span>
                  </div>

                  <div v-else class="text-muted mt-2" style="font-size: 0.82rem">
                    No students found.
                  </div>

                  <div v-if="manualRecipients.length > 0" class="d-flex flex-wrap align-items-center mt-2" style="gap: 6px">
                    <span
                      v-for="phone in manualRecipients"
                      :key="phone"
                      class="badge badge-light d-inline-flex align-items-center"
                      style="font-size: 0.72rem; padding: 5px 9px; font-weight: 400; color: #495057; border: 1px solid #dee2e6"
                    >
                      <span
                        class="rounded-circle d-inline-flex align-items-center justify-content-center mr-1"
                        style="width: 18px; height: 18px; background: #e9ecef; font-size: 0.62rem; font-weight: 600"
                      >
                        {{ manualRecipientInitial(phone) }}
                      </span>
                      {{ phone }}
                      <button
                        type="button"
                        class="btn btn-link p-0 ml-1 text-muted"
                        style="line-height: 1"
                        @click="removeManualRecipient(phone)"
                      >
                        <i class="material-icons" style="font-size: 14px">close</i>
                      </button>
                    </span>
                  </div>
                </div>

                <div v-else-if="audienceType === 'program' && !form.program" class="text-muted" style="font-size: 0.82rem">
                  Select a program to view recipients.
                </div>

                <div v-else-if="dashboardStudents.length > 0">
                  <div class="d-flex flex-wrap align-items-center" style="gap: 6px">
                    <span
                      v-for="student in visibleStudents"
                      :key="recipientKey(student)"
                      class="badge badge-light d-inline-flex align-items-center"
                      style="font-size: 0.72rem; padding: 4px 8px; font-weight: 400; color: #495057; border: 1px solid #dee2e6"
                    >
                      <span
                        class="rounded-circle d-inline-flex align-items-center justify-content-center mr-1"
                        style="width: 20px; height: 20px; background: #e9ecef; font-size: 0.62rem; font-weight: 600; overflow: hidden"
                      >
                        <img
                          v-if="recipientAvatar(student)"
                          :src="recipientAvatar(student)"
                          :alt="recipientName(student)"
                          style="width: 100%; height: 100%; object-fit: cover"
                        />
                        <span v-else>{{ recipientInitial(student) }}</span>
                      </span>
                      <span class="d-inline-flex flex-column align-items-start" style="line-height: 1.15">
                        <span style="font-weight: 500">{{ recipientName(student) }}</span>
                        <span class="text-muted">{{ recipientPhone(student) || 'No phone' }}</span>
                      </span>
                    </span>
                    <b-btn
                      v-if="dashboardStudents.length > 5"
                      variant="link"
                      size="sm"
                      class="p-0 text-primary"
                      @click="toggleShowAllStudents"
                      style="font-size: 0.7rem; text-decoration: none; font-weight: 500"
                    >
                      {{ showAllStudents ? 'See less' : `+${dashboardStudents.length - 5} more` }}
                    </b-btn>
                  </div>
                </div>
                <div v-else-if="audienceType !== 'manual' && isDashboardStudentsLoading">
                  <b-spinner small class="mr-2" />
                  <span class="text-muted" style="font-size: 0.9rem">Loading students...</span>
                </div>
                <div v-else-if="audienceType !== 'manual'" class="text-muted" style="font-size: 0.82rem">
                  No recipients found for the selected audience.
                </div>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="text-muted mb-0" style="font-size: 0.85rem; font-weight: 500">Message</label>
              <small class="text-muted">
                {{ smsCharCount }} chars · {{ smsSegmentCount }} SMS segment{{ smsSegmentCount > 1 ? 's' : '' }}
              </small>
            </div>
            <b-form-textarea
              v-model.trim="form.message"
              rows="8"
              max-rows="16"
              placeholder="Type your SMS message..."
              :state="$v.form.message.$dirty ? !$v.form.message.$error : null"
            />
          </div>

          <div
            v-if="audienceType === 'manual' && manualNumberInput && !manualRecipients.length"
            class="alert alert-danger py-2 px-3 mb-2"
            style="font-size: 0.8rem"
          >
            <i class="material-icons" style="font-size: 16px; vertical-align: middle">error_outline</i>
            Please enter at least one valid phone number.
          </div>
          <div v-if="$v.form.message.$dirty && $v.form.message.$error" class="alert alert-danger py-2 px-3 mb-2" style="font-size: 0.8rem">
            <i class="material-icons" style="font-size: 16px; vertical-align: middle">error_outline</i>
            Message is required.
          </div>
        </div>

        <b-form-group v-if="isSuperAdmin" label="Organization ID" label-for="organization" class="px-4 mb-4">
          <b-form-input
            id="organization"
            v-model.number="form.organization"
            type="number"
            placeholder="Enter organization ID"
          />
        </b-form-group>

        <div class="d-flex justify-content-end align-items-center px-4 py-3 border-top" style="gap: 10px; background: #f8f9fa">
          <b-btn variant="light" size="sm" @click="$bvModal.hide('compose-bulk-message-modal')" style="font-size: 0.85rem">
            Cancel
          </b-btn>
          <b-btn variant="primary" size="sm" type="submit" :disabled="isSending" style="font-size: 0.85rem">
            <b-spinner small v-if="isSending" class="mr-1" />
            {{ isSending ? 'Sending...' : 'Send SMS' }}
          </b-btn>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      id="delivery-log-modal"
      :title="get(selectedMessage, 'subject') || 'SMS Delivery Log'"
      size="xl"
      centered
      ok-only
      ok-title="Close"
      ok-variant="light"
      @hidden="onDeliveryLogHidden"
    >
      <div v-if="selectedMessage">
        <div class="alert alert-info py-2 px-3 d-flex align-items-center" v-if="isDeliveryPolling">
          <b-spinner small class="mr-2" />
          Tracking delivery status...
        </div>

        <div class="row text-center mb-4">
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body py-3">
                <span :class="statusBadgeClass(selectedMessage.status)" style="font-size: 0.8rem">
                  {{ statusLabel(selectedMessage.status) }}
                </span>
                <small class="d-block text-muted mt-1">Status</small>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body py-3">
                <h5 class="mb-0">{{ selectedMessage.total_recipients || 0 }}</h5>
                <small class="text-muted">Total</small>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="selectedMessage.send_type === 'sms'">
            <div class="card h-100">
              <div class="card-body py-3">
                <h5 class="mb-0 text-success">{{ selectedMessage.sms_count || 0 }}</h5>
                <small class="text-muted">SMS Sent</small>
              </div>
            </div>
          </div>
        </div>

        <ul class="list-group list-group-flush mb-3">
          <li class="list-group-item d-flex justify-content-between px-0">
            <span class="text-muted">Program</span>
            <span>{{ get(selectedMessage, 'program.title') || 'All Students' }}</span>
          </li>
          <li class="list-group-item d-flex justify-content-between px-0">
            <span class="text-muted">Sent by</span>
            <span>{{ selectedMessage.sent_by_name || '—' }}</span>
          </li>
          <li class="list-group-item d-flex justify-content-between px-0">
            <span class="text-muted">Date</span>
            <span>{{ formatDateTime(selectedMessage.created_at) }}</span>
          </li>
        </ul>

        <div v-if="selectedMessage.send_type === 'sms'" class="mt-4">
          <h6 class="mb-2">
            <i class="material-icons icon-16pt mr-1" style="vertical-align: middle">sms</i>
            SMS Delivery Log
          </h6>
          <b-skeleton-wrapper :loading="isLogsLoading">
            <template #loading>
              <b-skeleton-table :rows="4" :columns="5" />
            </template>
            <b-table
              :items="smsLogs"
              :fields="smsLogFields"
              responsive
              small
              bordered
              striped
              :busy="isLogsLoading"
              show-empty
              empty-text="No SMS log entries."
            >
              <template #cell(status)="{ item }">
                <span
                  :class="statusBadgeClass(item.status)"
                  style="text-transform: none"
                >
                  {{ statusLabel(item.status) }}
                </span>
              </template>
              <template #cell(created_at)="{ item }">{{ formatDateTime(item.created_at) }}</template>
            </b-table>
            <pagination
              v-if="totalSmsLogs > smsLogs.length"
              v-model="smsLogsPage"
              :total-rows="totalSmsLogs"
              :per-page="logsPerPage"
              @change="onSmsLogsPageChange"
            />
          </b-skeleton-wrapper>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="thread-panel-modal"
      :title="selectedConversationTitle"
      size="lg"
      centered
      hide-footer
      @hidden="onThreadModalHidden"
    >
      <b-skeleton-wrapper :loading="isThreadLoading">
        <template #loading>
          <b-skeleton width="60%" class="mb-3" />
          <b-skeleton width="40%" class="mb-4" />
          <b-skeleton-table :rows="3" :columns="1" />
        </template>

        <div v-if="selectedConversation">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h6 class="mb-1">{{ selectedConversationTitle }}</h6>
              <small class="text-muted d-block">Student: {{ selectedConversation.participant_phone || 'No phone' }}</small>
              <small v-if="selectedConversation.organization_phone" class="text-muted d-block"
                >Org Sender: {{ selectedConversation.organization_phone }}</small
              >
            </div>
            <div class="d-flex" style="gap: 6px">
              <b-badge v-if="selectedConversation.is_unknown_sender" variant="secondary">Unknown</b-badge>
              <b-badge v-if="!selectedConversation.is_read" variant="primary">Unread</b-badge>
            </div>
          </div>

          <div class="thread-messages-container mb-3" ref="threadMessagesContainer">
            <div v-if="!selectedConversationMessages.length" class="text-muted text-center py-4">No messages yet.</div>

            <div
              v-for="message in selectedConversationMessages"
              :key="message.id"
              class="d-flex mb-2"
              :class="message.direction === 'outbound' ? 'justify-content-end' : 'justify-content-start'"
            >
              <div
                class="sms-bubble"
                :class="[
                  message.direction === 'outbound' ? 'sms-bubble-outbound' : 'sms-bubble-inbound',
                  message.status === 'failed' ? 'sms-bubble-failed' : ''
                ]"
              >
                <div
                  v-if="message.direction === 'inbound' && (message.sender_user_name || message.sender_phone)"
                  class="sms-bubble-sender"
                >
                  {{ message.sender_user_name || message.sender_phone }}
                </div>
                <div class="sms-bubble-body">{{ message.body }}</div>
                <div class="sms-bubble-meta">
                  <span>{{ formatDateTime(message.received_at || message.created_at) }}</span>
                  <span v-if="message.status === 'sending'" class="ml-2 text-info">Sending...</span>
                  <span v-else-if="message.status === 'failed'" class="ml-2 text-danger">Failed</span>
                </div>
                <div v-if="message.error_message" class="sms-bubble-error">{{ message.error_message }}</div>
              </div>
            </div>
          </div>

          <div>
            <label for="thread-reply-body" class="font-weight-bold mb-2">Reply as Organization</label>
            <b-form-textarea
              id="thread-reply-body"
              v-model.trim="threadReplyBody"
              rows="4"
              max-rows="10"
              placeholder="Write your SMS reply..."
            />
            <div class="d-flex justify-content-end align-items-center mt-3" style="gap: 8px">
              <b-btn variant="light" @click="$bvModal.hide('thread-panel-modal')">Close</b-btn>
              <b-btn
                variant="primary"
                :disabled="isThreadReplySending || !threadReplyBody || !selectedConversation"
                @click="submitThreadReply"
              >
                <b-spinner small v-if="isThreadReplySending" class="mr-2" />
                {{ isThreadReplySending ? 'Sending...' : 'Send Reply' }}
              </b-btn>
            </div>
          </div>
        </div>
      </b-skeleton-wrapper>
    </b-modal>
  </div>
</template>

<script>
import { debounce, get, trim, truncate } from 'lodash';
import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { mapActions, mapGetters } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { USER_ROLES, USER_ROLE_TYPES } from '@/common/constants';
import { formatDateTime } from '@/common/utils';

export default {
  components: { Pagination, PageSeparator },
  extends: Page,

  data() {
    return {
      title: 'Bulk SMS',
      USER_ROLES,
      USER_ROLE_TYPES,

      activeTab: 0,

      isLoading: false,
      isSending: false,
      areProgramsLoading: false,
      perPage: 14,
      currentPage: 1,
      totalMessages: 0,
      pageFromCount: 0,
      pageToCount: 0,
      messages: [],
      searchTerm: '',
      filters: {
        status: null,
        program: null,
      },
      allPrograms: [],
      selectedMessage: null,

      isLogsLoading: false,
      logsPerPage: 20,
      smsLogs: [],
      totalSmsLogs: 0,
      smsLogsPage: 1,
      isDeliveryPolling: false,
      deliveryPollTimer: null,
      deliveryPollStartedAt: null,

      isInboxLoading: false,
      allConversations: [],
      conversations: [],
      totalConversations: 0,
      inboxCurrentPage: 1,
      inboxPageFrom: 0,
      inboxPageTo: 0,
      inboxSearch: '',
      inboxUnreadOnly: false,
      inboxUnreadCount: 0,
      inboxBulkMessageId: null,
      inboxPollTimer: null,
      inboxPollStartedAt: null,
      inboxPollDurationMs: 180000,

      selectedConversation: null,
      selectedConversationMessages: [],
      isThreadLoading: false,
      threadReplyBody: '',
      isThreadReplySending: false,
      threadPollTimer: null,
      threadPollStartedAt: null,
      threadPollDurationMs: 120000,

      audienceType: 'all',
      dashboardStudents: [],
      isDashboardStudentsLoading: false,
      showAllStudents: false,
      manualNumberInput: '',
      manualNumberTags: [],
      selectedManualStudents: [],

      form: {
        message: '',
        send_type: 'sms',
        program: null,
        manual_numbers: '',
        organization: null,
      },

      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: 'pending', text: 'Pending' },
        { value: 'processing', text: 'Processing' },
        { value: 'sent', text: 'Sent' },
        { value: 'failed', text: 'Failed' },
      ],

      smsLogFields: [
        { key: 'recipient_user_name', label: 'Name' },
        { key: 'recipient_phone', label: 'Phone' },
        { key: 'status', label: 'Status' },
        { key: 'twilio_sid', label: 'Twilio SID' },
        { key: 'created_at', label: 'Sent At' },
      ],
    };
  },

  validations() {
    return {
      form: {
        message: { required },
        send_type: { required },
        organization: {
          required: requiredIf(() => this.isSuperAdmin),
        },
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Bulk SMS', active: true },
      ];
    },

    visibleStudents() {
      if (this.showAllStudents) return this.dashboardStudents;
      return this.dashboardStudents.slice(0, 5);
    },

    smsCharCount() {
      return (this.form.message || '').length;
    },

    smsSegmentCount() {
      return Math.max(1, Math.ceil(this.smsCharCount / 160));
    },

    manualRecipients() {
      const selectedStudentNumbers = (this.selectedManualStudents || [])
        .map(item => this.normalizeManualPhone(item.phone))
        .filter(Boolean);
      const manualNumbers = (this.manualNumberTags || []).map(phone => this.normalizeManualPhone(phone)).filter(Boolean);
      return Array.from(new Set([...selectedStudentNumbers, ...manualNumbers]));
    },

    manualNumberSuggestions() {
      const term = trim(this.manualNumberInput || '').toLowerCase();
      if (!term) return [];

      const selected = new Set(this.manualRecipients);

      const filtered = (this.dashboardStudents || []).filter(student => {
        const phone = this.normalizeManualPhone(this.recipientPhone(student));
        if (!phone || selected.has(phone)) return false;

        const name = this.recipientName(student);
        const displayPhone = this.recipientPhone(student) || '';
        return `${name} ${displayPhone}`.toLowerCase().includes(term);
      });

      return filtered.slice(0, 10);
    },

    selectedConversationTitle() {
      if (!this.selectedConversation) return 'SMS Conversation';
      return this.threadDisplayName(this.selectedConversation);
    },
  },

  methods: {
    ...mapActions('bulkMessaging', [
      'getBulkMessages',
      'getBulkMessage',
      'createBulkMessage',
      'getBulkMessageSmsLogs',
      'getSmsRepliesThreadedList',
      'getSmsReply',
      'replyToSmsReply',
      'markSmsReplyRead',
      'getBulkMessageDashboardStudents',
    ]),
    ...mapActions('program', ['getAllPrograms']),
    get,
    trim,
    truncate,
    formatDateTime,

    unwrapData(response) {
      if (response && Object.prototype.hasOwnProperty.call(response, 'data')) {
        return response.data;
      }
      return response;
    },

    normalizeListResponse(response) {
      const payload = this.unwrapData(response);

      if (Array.isArray(payload)) {
        return { results: payload, count: payload.length };
      }

      if (payload && Array.isArray(payload.results)) {
        return { results: payload.results, count: payload.count || payload.results.length };
      }

      return { results: [], count: 0 };
    },

    normalizeObjectResponse(response) {
      const payload = this.unwrapData(response);
      if (payload && typeof payload === 'object') return payload;
      return {};
    },

    extractErrorMessage(err) {
      return (
        get(err, 'response.data.message') ||
        get(err, 'response.data.detail') ||
        get(err, 'message') ||
        this.$t('generalMsgs.genErrorMsg')
      );
    },

    stripHtml(html) {
      if (!html) return '';
      const tokens = [];
      let result = html.replace(/{{(.*?)}}/g, (_, key) => {
        tokens.push(key);
        return `__BOOKMARK_${tokens.length - 1}__`;
      });
      result = result
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"');
      result = result.replace(/__BOOKMARK_(\d+)__/g, (_, i) => `{{${tokens[i]}}}`);
      return result.trim();
    },

    sendTypeLabel(type) {
      const map = { sms: 'SMS' };
      return map[type] || type;
    },

    sendTypeIcon(type) {
      const map = { sms: 'sms' };
      return map[type] || 'send';
    },

    statusLabel(status) {
      const map = { pending: 'Pending', processing: 'Processing', sent: 'Sent', failed: 'Failed', error: 'Error' };
      return map[status] || status;
    },

    statusBadgeClass(status) {
      const map = {
        pending: 'badge badge-warning',
        processing: 'badge badge-info',
        sent: 'badge badge-success',
        failed: 'badge badge-danger',
        error: 'badge badge-danger',
      };
      return map[status] || 'badge badge-secondary';
    },

    sentStatusLabel(msg) {
      if (msg.status !== 'sent') return null;
      const sent = msg.send_type === 'sms' ? msg.sms_count : msg.email_count;
      const total = msg.total_recipients;
      if (!total || sent === total || sent === 0) return null;
      return `Partial (${sent}/${total})`;
    },

    sentStatusBadgeClass(msg) {
      if (msg.status !== 'sent') return 'd-none';
      const sent = msg.send_type === 'sms' ? msg.sms_count : msg.email_count;
      const total = msg.total_recipients;
      if (!total || sent === total || sent === 0) return 'd-none';
      return 'badge badge-warning';
    },

    deliveryCountLabel(msg) {
      const total = msg.total_recipients || 0;
      return `${msg.sms_count || 0}/${total} delivered`;
    },

    parseThreadReplies(conversation) {
      return (Array.isArray(conversation.replies) ? conversation.replies : [])
        .slice()
        .sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
    },

    normalizeThread(conversation) {
      const replies = this.parseThreadReplies(conversation);
      const lastReply = replies.length ? replies[replies.length - 1] : null;
      return {
        ...conversation,
        id: conversation.id || conversation.log_id,
        replies,
        student_name: conversation.sender_user_name || conversation.student_name || null,
        participant_phone: conversation.sender_phone || conversation.participant_phone || null,
        is_unknown_sender: Boolean(conversation.is_unknown_sender || (!conversation.sender_user && !conversation.sender_user_name)),
        reply_count: conversation.reply_count || replies.length,
        last_message_preview: get(lastReply, 'body', ''),
        last_message_at: conversation.latest_reply_at || get(lastReply, 'created_at') || conversation.last_message_at || null,
      };
    },

    threadDisplayName(conversation) {
      return conversation.sender_user_name || conversation.student_name || conversation.sender_phone || 'Unknown Sender';
    },

    threadParticipantPhone(conversation) {
      return conversation.sender_phone || conversation.participant_phone || null;
    },

    threadLastMessage(conversation) {
      const replies = this.parseThreadReplies(conversation);
      return replies.length ? replies[replies.length - 1] : null;
    },

    threadLastMessageAt(conversation) {
      const lastReply = this.threadLastMessage(conversation);
      return get(lastReply, 'created_at') || conversation.latest_reply_at || conversation.last_message_at || null;
    },

    applyInboxSearch(threads) {
      const term = trim(this.inboxSearch || '').toLowerCase();
      if (!term) return threads;
      return threads.filter(thread => {
        const haystack = [
          this.threadDisplayName(thread),
          this.threadParticipantPhone(thread),
          get(this.threadLastMessage(thread), 'body', ''),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
    },

    updateInboxView(threads, pageNum = 1) {
      const filteredThreads = this.applyInboxSearch(threads || []);
      this.totalConversations = filteredThreads.length;

      const totalPages = Math.max(1, Math.ceil(this.totalConversations / this.perPage));
      const safePage = Math.min(Math.max(1, pageNum), totalPages);
      this.inboxCurrentPage = safePage;

      const start = (safePage - 1) * this.perPage;
      const end = start + this.perPage;
      this.conversations = filteredThreads.slice(start, end);

      if (this.totalConversations > 0) {
        this.inboxPageFrom = (safePage - 1) * this.perPage + 1;
        this.inboxPageTo = this.inboxPageFrom + this.conversations.length - 1;
      } else {
        this.inboxPageFrom = 0;
        this.inboxPageTo = 0;
      }
    },

    findThreadByIdentifier(identifier, threads = this.allConversations) {
      if (!identifier) return null;
      const key = `${identifier}`;
      return (threads || []).find(thread => {
        if (`${thread.log_id}` === key || `${thread.id}` === key) return true;
        return this.parseThreadReplies(thread).some(reply => `${reply.id}` === key);
      });
    },

    replyInitial(conversation) {
      const value = this.threadDisplayName(conversation) || '?';
      return value.charAt(0).toUpperCase();
    },

    recipientName(student) {
      return student.name || student.student_name || student.full_name || 'Unknown Student';
    },

    recipientPhone(student) {
      return student.student_phone || student.phone || student.mobile_number || null;
    },

    recipientAvatar(student) {
      return student.avatar_url || student.avatar || student.profile_pic_url || null;
    },

    recipientInitial(student) {
      const value = this.recipientName(student) || this.recipientPhone(student) || '?';
      return value.charAt(0).toUpperCase();
    },

    recipientKey(student) {
      return student.student_id || student.user_id || student.id || this.recipientPhone(student) || student.email;
    },

    normalizeManualPhone(phone) {
      const raw = trim(phone || '');
      if (!raw) return null;

      const hasLeadingPlus = raw.startsWith('+');
      const digits = raw.replace(/\D/g, '');
      if (digits.length < 7 || digits.length > 15) return null;

      return hasLeadingPlus ? `+${digits}` : digits;
    },

    parseManualNumbers(value) {
      const raw = typeof value === 'string' ? value : '';
      const tokens = raw.split(/[\n,;]+/).map(item => this.normalizeManualPhone(item)).filter(Boolean);
      return Array.from(new Set(tokens));
    },

    syncManualNumbersField() {
      this.form.manual_numbers = this.manualRecipients.join('\n');
    },

    addManualRecipient(phone) {
      const normalized = this.normalizeManualPhone(phone);
      if (!normalized) return false;
      if (this.manualRecipients.includes(normalized)) return true;

      this.manualNumberTags = [...this.manualRecipients, normalized];
      this.syncManualNumbersField();
      return true;
    },

    addManualRecipientFromStudent(student) {
      const phone = this.normalizeManualPhone(this.recipientPhone(student));
      const key = this.recipientKey(student);
      if (!phone || !key) {
        this.makeToast({ variant: 'warning', msg: 'Selected student does not have a valid phone number.' });
        return;
      }

      if (!this.selectedManualStudents.some(item => item.key === key)) {
        this.selectedManualStudents = [...this.selectedManualStudents, { key, phone }];
      }

      this.manualNumberInput = '';
      this.syncManualNumbersField();
    },

    isManualRecipientSelected(student) {
      const key = this.recipientKey(student);
      return !!key && this.selectedManualStudents.some(item => item.key === key);
    },

    toggleManualRecipientFromStudent(student) {
      const phone = this.normalizeManualPhone(this.recipientPhone(student));
      const key = this.recipientKey(student);
      if (!phone) {
        this.makeToast({ variant: 'warning', msg: 'Selected student does not have a valid phone number.' });
        return;
      }
      if (!key) return;

      if (this.selectedManualStudents.some(item => item.key === key)) {
        this.selectedManualStudents = this.selectedManualStudents.filter(item => item.key !== key);
      } else {
        this.selectedManualStudents = [...this.selectedManualStudents, { key, phone }];
      }
      this.syncManualNumbersField();
    },

    addManualRecipientsFromInput() {
      const raw = trim(this.manualNumberInput || '');
      if (!raw) return;

      const suggestionMatch = (this.dashboardStudents || []).find(student => {
        const name = this.recipientName(student).toLowerCase();
        const phone = this.normalizeManualPhone(this.recipientPhone(student));
        const needle = raw.toLowerCase();
        return name === needle || (phone && phone.toLowerCase() === needle);
      });
      if (suggestionMatch) {
        this.addManualRecipientFromStudent(suggestionMatch);
        return;
      }

      const parsed = this.parseManualNumbers(raw);
      if (!parsed.length) {
        this.makeToast({ variant: 'warning', msg: 'Please enter a valid phone number.' });
        return;
      }

      parsed.forEach(phone => this.addManualRecipient(phone));
      this.manualNumberInput = '';
    },

    removeManualRecipient(phone) {
      this.manualNumberTags = this.manualRecipients.filter(item => item !== phone);
      const normalized = this.normalizeManualPhone(phone);
      this.selectedManualStudents = this.selectedManualStudents.filter(item => item.phone !== normalized);
      this.syncManualNumbersField();
    },

    manualRecipientInitial(phone) {
      const value = trim(phone || '');
      if (!value) return '#';
      const lastDigit = value.replace(/\D/g, '').slice(-1);
      return lastDigit || '#';
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms({ ordering: 'id' });
        this.allPrograms = get(res, 'data.results', get(res, 'data', []));
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    onFilterApplied() {
      this.fetchMessages();
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    onManualNumberInput() {
      if (this.audienceType !== 'manual') return;
      this.debouncedManualNumberSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchMessages();
    }, 500),

    debouncedManualNumberSearch: debounce(vm => {
      vm.fetchDashboardStudents(vm.manualNumberInput);
    }, 350),

    async fetchMessages(pageNum = 1) {
      this.isLoading = true;
      if (document.getElementById('page-container')) {
        document.getElementById('page-container').scrollIntoView();
      }
      try {
        const params = {
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ordering: '-created_at',
          send_type: 'sms',
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.filters.status && { status: this.filters.status }),
          ...(this.filters.program && { program: this.filters.program }),
        };
        const response = await this.getBulkMessages(params);
        const { results, count } = this.normalizeListResponse(response);
        this.messages = results;
        this.currentPage = pageNum;
        this.totalMessages = count;

        if (count > 0) {
          this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
          this.pageToCount = this.pageFromCount + get(this.messages, 'length', 0) - 1;
        } else {
          this.pageFromCount = 0;
          this.pageToCount = 0;
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchMessages(pageNum);
    },

    async openDeliveryLogDrawer(msg, options = { startPolling: true }) {
      this.selectedMessage = { ...msg };
      this.smsLogs = [];
      this.totalSmsLogs = 0;
      this.smsLogsPage = 1;
      this.$bvModal.show('delivery-log-modal');
      await this.refreshDeliveryDetails();

      if (options.startPolling !== false) {
        this.startDeliveryPolling();
      }
    },

    shouldPollStatus(status) {
      return status === 'pending' || status === 'processing';
    },

    async refreshDeliveryDetails() {
      if (!this.selectedMessage || !this.selectedMessage.id) return;

      try {
        const summaryResponse = await this.getBulkMessage(this.selectedMessage.id);
        const latest = this.normalizeObjectResponse(summaryResponse);
        if (latest && latest.id) {
          this.selectedMessage = { ...this.selectedMessage, ...latest };
        }
      } catch (e) {
        // non-blocking for logs
      }

      await this.fetchDeliveryLogs();
    },

    async fetchDeliveryLogs() {
      if (!this.selectedMessage) return;
      this.isLogsLoading = true;
      try {
        const id = this.selectedMessage.id;
        if (this.selectedMessage.send_type === 'sms') {
          const res = await this.getBulkMessageSmsLogs({
            id,
            params: { limit: this.logsPerPage, offset: (this.smsLogsPage - 1) * this.logsPerPage },
          });
          const parsed = this.normalizeListResponse(res);
          this.smsLogs = parsed.results;
          this.totalSmsLogs = parsed.count;
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLogsLoading = false;
    },

    startDeliveryPolling() {
      this.stopDeliveryPolling();
      if (!this.selectedMessage || !this.shouldPollStatus(this.selectedMessage.status)) {
        this.isDeliveryPolling = false;
        return;
      }

      this.isDeliveryPolling = true;
      this.deliveryPollStartedAt = Date.now();

      const poll = async () => {
        if (!this.selectedMessage) {
          this.stopDeliveryPolling();
          return;
        }

        const elapsed = Date.now() - this.deliveryPollStartedAt;
        if (elapsed >= 60000) {
          this.stopDeliveryPolling();
          return;
        }

        await this.refreshDeliveryDetails();

        if (!this.shouldPollStatus(this.selectedMessage.status)) {
          this.stopDeliveryPolling();
          await this.fetchMessages(this.currentPage);
          return;
        }

        this.deliveryPollTimer = setTimeout(poll, 5000);
      };

      this.deliveryPollTimer = setTimeout(poll, 5000);
    },

    stopDeliveryPolling() {
      clearTimeout(this.deliveryPollTimer);
      this.deliveryPollTimer = null;
      this.isDeliveryPolling = false;
      this.deliveryPollStartedAt = null;
    },

    onDeliveryLogHidden() {
      this.stopDeliveryPolling();
    },

    async onSmsLogsPageChange(page) {
      this.smsLogsPage = page;
      await this.fetchDeliveryLogs();
    },

    openInboxTab(bulkMessageId = this.inboxBulkMessageId) {
      this.activeTab = 1;
      this.inboxBulkMessageId = bulkMessageId || null;
      this.fetchConversations(1);
      this.fetchUnreadCount();
      this.startInboxPoll();
    },

    onTabChange(tab) {
      if (tab === 1) {
        this.openInboxTab(this.inboxBulkMessageId);
      } else {
        this.stopInboxPoll();
      }
    },

    onInboxSearch() {
      this.debouncedInboxSearch(this);
    },

    debouncedInboxSearch: debounce(vm => {
      vm.updateInboxView(vm.allConversations, 1);
    }, 500),

    onInboxFilterApplied() {
      this.fetchConversations(1);
    },

    async fetchConversations(pageNum = 1) {
      this.isInboxLoading = true;
      try {
        const params = {
          page_size: 500,
          ...(this.inboxUnreadOnly && { is_read: false }),
          ...(this.inboxBulkMessageId && { bulk_message: this.inboxBulkMessageId }),
        };
        const response = await this.getSmsRepliesThreadedList(params);
        const { results } = this.normalizeListResponse(response);
        const normalizedThreads = (results || []).map(item => this.normalizeThread(item));

        this.allConversations = normalizedThreads;
        this.updateInboxView(normalizedThreads, pageNum);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isInboxLoading = false;
    },

    onInboxPageChange(page) {
      this.updateInboxView(this.allConversations, page);
    },

    async refreshInboxData() {
      await this.fetchConversations(this.inboxCurrentPage);
      await this.fetchUnreadCount();
    },

    async fetchUnreadCount() {
      try {
        const response = await this.getSmsRepliesThreadedList({ is_read: false, page_size: 1 });
        const { results, count } = this.normalizeListResponse(response);
        this.inboxUnreadCount = typeof count === 'number' ? count : (results || []).length;
      } catch (e) {
        // non-blocking
      }
    },

    startInboxPoll() {
      this.stopInboxPoll();
      this.inboxPollStartedAt = null;
    },

    stopInboxPoll() {
      clearTimeout(this.inboxPollTimer);
      this.inboxPollTimer = null;
      this.inboxPollStartedAt = null;
    },

    async openConversationThread(conversation) {
      const normalizedConversation = this.normalizeThread(conversation);
      this.selectedConversation = { ...normalizedConversation };
      this.selectedConversationMessages = normalizedConversation.replies || [];
      this.threadReplyBody = '';
      this.isThreadLoading = true;

      this.$bvModal.show('thread-panel-modal');

      await this.refreshThreadFromThreadedList(false);
      await this.markConversationAsRead();
      this.scrollThreadToBottom();
      this.startThreadPoll();
      this.isThreadLoading = false;
    },

    async openConversationById(conversationId) {
      if (!conversationId) return;

      let localConversation = this.findThreadByIdentifier(conversationId, this.conversations);
      if (!localConversation) {
        localConversation = this.findThreadByIdentifier(conversationId, this.allConversations);
      }

      if (localConversation) {
        await this.openConversationThread(localConversation);
        return;
      }

      try {
        await this.fetchConversations(1);
        localConversation = this.findThreadByIdentifier(conversationId, this.allConversations);

        const isNumericIdentifier = /^\d+$/.test(`${conversationId}`);
        if (!localConversation && isNumericIdentifier) {
          const replyResponse = await this.getSmsReply(conversationId);
          const reply = this.normalizeObjectResponse(replyResponse);
          if (reply && (reply.log_id || reply.id)) {
            localConversation = this.findThreadByIdentifier(reply.log_id || reply.id, this.allConversations);
          }
        }

        if (localConversation) {
          await this.openConversationThread(localConversation);
          return;
        }

        this.makeToast({ variant: 'warning', msg: 'Conversation not found in inbox.' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.extractErrorMessage(e) });
      }
    },

    async handleInboxQueryNavigation() {
      if (this.$route.query.tab !== 'inbox') return;

      this.activeTab = 1;
      this.inboxBulkMessageId = this.$route.query.bulkMessageId || null;
      await this.fetchConversations(1);

      const conversationId = this.$route.query.conversationId;
      if (conversationId) {
        await this.openConversationById(conversationId);
      }

      this.startInboxPoll();
    },

    async refreshThreadFromThreadedList(silent = true) {
      if (!this.selectedConversation) return;

      try {
        const params = {
          page_size: 500,
          ...(this.inboxBulkMessageId && { bulk_message: this.inboxBulkMessageId }),
        };
        const response = await this.getSmsRepliesThreadedList(params);
        const { results } = this.normalizeListResponse(response);
        const normalizedThreads = (results || []).map(item => this.normalizeThread(item));

        this.allConversations = normalizedThreads;
        this.updateInboxView(this.allConversations, this.inboxCurrentPage);

        const selectedId = this.selectedConversation.log_id || this.selectedConversation.id;
        const updatedThread = this.findThreadByIdentifier(selectedId, normalizedThreads);
        if (!updatedThread) {
          if (!silent) {
            this.makeToast({ variant: 'warning', msg: 'Conversation not found.' });
          }
          return;
        }

        this.selectedConversation = { ...updatedThread };
        this.selectedConversationMessages = this.parseThreadReplies(updatedThread);
        this.syncConversationInList(this.selectedConversation);
        this.scrollThreadToBottom();
      } catch (e) {
        if (!silent) {
          this.makeToast({ variant: 'danger', msg: this.extractErrorMessage(e) });
        }
      }
    },

    syncConversationInList(conversation) {
      const key = `${conversation.log_id || conversation.id}`;

      const updateCollection = collection => {
        const index = collection.findIndex(item => `${item.log_id || item.id}` === key);
        if (index !== -1) {
          const merged = this.normalizeThread({
            ...collection[index],
            ...conversation,
          });
          this.$set(collection, index, merged);
        }
      };

      updateCollection(this.allConversations);
      updateCollection(this.conversations);

      this.inboxUnreadCount = this.allConversations.filter(item => !item.is_read).length;
    },

    async markConversationAsRead() {
      if (!this.selectedConversation) return;

      const unreadReplies = this.parseThreadReplies(this.selectedConversation).filter(reply => !reply.is_read && reply.id);
      if (!unreadReplies.length) return;

      try {
        await Promise.allSettled(unreadReplies.map(reply => this.markSmsReplyRead(reply.id)));

        const unreadIds = unreadReplies.map(reply => `${reply.id}`);
        const updatedReplies = this.parseThreadReplies(this.selectedConversation).map(reply =>
          unreadIds.includes(`${reply.id}`) ? { ...reply, is_read: true } : reply
        );

        this.$set(this.selectedConversation, 'replies', updatedReplies);
        this.$set(this.selectedConversation, 'is_read', true);
        this.selectedConversationMessages = updatedReplies;
        this.syncConversationInList(this.selectedConversation);
      } catch (e) {
        // non-critical
      }
    },

    startThreadPoll() {
      this.stopThreadPoll();
      this.threadPollStartedAt = null;
    },

    stopThreadPoll() {
      clearTimeout(this.threadPollTimer);
      this.threadPollTimer = null;
      this.threadPollStartedAt = null;
    },

    onThreadModalHidden() {
      this.stopThreadPoll();
      this.selectedConversation = null;
      this.selectedConversationMessages = [];
      this.threadReplyBody = '';
      this.isThreadReplySending = false;
      this.isThreadLoading = false;
    },

    scrollThreadToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.threadMessagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    replaceThreadMessage(tempId, replacement) {
      const index = this.selectedConversationMessages.findIndex(item => item.id === tempId);
      if (index !== -1) {
        this.$set(this.selectedConversationMessages, index, replacement);
      } else {
        this.selectedConversationMessages.push(replacement);
      }
    },

    async submitThreadReply() {
      if (!this.selectedConversation) return;

      const body = trim(this.threadReplyBody || '');
      if (!body) return;

      const replyTarget = this.threadLastMessage(this.selectedConversation);
      const replyId = get(replyTarget, 'id');
      if (!replyId) {
        this.makeToast({ variant: 'warning', msg: 'Unable to determine conversation reply target.' });
        return;
      }

      const optimisticId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: optimisticId,
        log_id: this.selectedConversation.log_id,
        direction: 'outbound',
        body,
        status: 'sending',
        created_at: new Date().toISOString(),
      };

      this.selectedConversationMessages = [...this.selectedConversationMessages, optimisticMessage];
      this.threadReplyBody = '';
      this.isThreadReplySending = true;
      this.scrollThreadToBottom();

      try {
        await this.replyToSmsReply({ id: replyId, body });
        this.replaceThreadMessage(
          optimisticId,
          {
            ...optimisticMessage,
            status: 'sent',
          }
        );

        await this.refreshThreadFromThreadedList(true);
        await this.fetchConversations(this.inboxCurrentPage);
        await this.fetchUnreadCount();
      } catch (err) {
        const statusCode = get(err, 'response.status');
        const statusOnlyMessage = statusCode
          ? `Request failed (${statusCode})`
          : this.$t('generalMsgs.genErrorMsg');
        const failedPayload = this.normalizeObjectResponse(get(err, 'response.data', {}));
        if (failedPayload && failedPayload.id) {
          this.replaceThreadMessage(optimisticId, {
            ...failedPayload,
            status: 'failed',
            error_message: statusOnlyMessage,
          });
        } else {
          this.replaceThreadMessage(optimisticId, {
            ...optimisticMessage,
            status: 'failed',
            error_message: statusOnlyMessage,
          });
        }

        this.makeToast({ variant: 'danger', msg: statusOnlyMessage });
      }

      this.isThreadReplySending = false;
    },

    openComposeModal() {
      this.$bvModal.show('compose-bulk-message-modal');
      this.fetchDashboardStudents();
    },

    async fetchDashboardStudents(searchValue = '') {
      if (this.audienceType === 'program' && !this.form.program) {
        this.dashboardStudents = [];
        this.isDashboardStudentsLoading = false;
        return;
      }

      this.isDashboardStudentsLoading = true;
      try {
        const params = {};
        if (this.audienceType === 'manual') {
          const term = trim(searchValue || this.manualNumberInput || '');
          if (term) {
            params.search = term;
          }
        } else if (this.audienceType === 'program' && this.form.program) {
          params.program = this.form.program;
        }
        const response = await this.getBulkMessageDashboardStudents(params);
        const parsed = this.normalizeListResponse(response);
        this.dashboardStudents = parsed.results;
      } catch (e) {
        this.dashboardStudents = [];
      }
      this.isDashboardStudentsLoading = false;
    },

    onAudienceTypeChange() {
      if (this.audienceType !== 'program') {
        this.form.program = null;
      }
      if (this.audienceType !== 'manual') {
        this.form.manual_numbers = '';
        this.manualNumberInput = '';
        this.manualNumberTags = [];
        this.selectedManualStudents = [];
      }
      this.showAllStudents = false;
      this.fetchDashboardStudents();
    },

    onProgramChange() {
      this.showAllStudents = false;
      this.fetchDashboardStudents();
    },

    toggleShowAllStudents() {
      this.showAllStudents = !this.showAllStudents;
    },

    resetForm() {
      this.form = {
        message: '',
        send_type: 'sms',
        program: null,
        manual_numbers: '',
        organization: null,
      };
      this.manualNumberInput = '';
      this.manualNumberTags = [];
      this.selectedManualStudents = [];
      this.audienceType = 'all';
      this.dashboardStudents = [];
      this.showAllStudents = false;
      this.$v.$reset();
    },

    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.makeToast({ variant: 'danger', msg: 'Please fill all required fields.' });
        return;
      }

      if (this.audienceType === 'program' && !this.form.program) {
        this.makeToast({ variant: 'warning', msg: 'Please select a program to continue.' });
        return;
      }

      if (this.audienceType === 'manual') {
        this.addManualRecipientsFromInput();
      }

      const manualNumbers = this.manualRecipients;
      if (this.audienceType === 'manual' && !manualNumbers.length) {
        this.makeToast({ variant: 'warning', msg: 'Please add at least one valid phone number.' });
        return;
      }

      this.isSending = true;
      try {
        const data = {
          message: this.form.message,
          send_type: 'sms',
          ...(this.audienceType === 'program' && this.form.program && { program: this.form.program }),
          ...(manualNumbers.length && { manual_numbers: manualNumbers }),
        };

        if (this.isSuperAdmin && this.form.organization) {
          data.organization = this.form.organization;
        } else if (!this.isSuperAdmin) {
          const orgId = get(this.getLoggedInUser, 'linked_entity.id');
          if (orgId) data.organization = orgId;
        }

        const response = await this.createBulkMessage(data);
        const createdPayload = this.normalizeObjectResponse(response);
        const created = get(createdPayload, 'data', createdPayload);

        this.makeToast({
          variant: 'success',
          msg: get(response, 'data.message', get(response, 'message', 'Bulk message queued for sending.')),
        });
        this.$bvModal.hide('compose-bulk-message-modal');

        if (created.id) {
          this.messages = [created, ...this.messages];
          this.totalMessages++;
          this.openDeliveryLogDrawer(created, { startPolling: true });
        } else {
          await this.fetchMessages(this.currentPage);
        }
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.extractErrorMessage(err) });
      }
      this.isSending = false;
    },
  },

  async mounted() {
    await this.fetchPrograms();
    await this.fetchMessages();

    await this.handleInboxQueryNavigation();

    if (this.activeTab !== 1) {
      await this.fetchUnreadCount();
    }
  },

  watch: {
    '$route.query.tab'(newVal) {
      if (newVal === 'inbox') {
        this.handleInboxQueryNavigation();
      } else {
        this.stopInboxPoll();
      }
    },

    '$route.query.bulkMessageId'(newVal) {
      if (this.$route.query.tab === 'inbox') {
        this.inboxBulkMessageId = newVal || null;
        this.fetchConversations(1);
      }
    },

    '$route.query.conversationId'(newVal) {
      if (this.$route.query.tab === 'inbox' && newVal) {
        this.openConversationById(newVal);
      }
    },
  },

  beforeDestroy() {
    this.stopDeliveryPolling();
    this.stopInboxPoll();
    this.stopThreadPoll();
  },
};
</script>

<style scoped>
.thread-messages-container {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  background: #fafbfc;
}

.sms-bubble {
  max-width: 82%;
  padding: 8px 10px;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.45;
  word-break: break-word;
}

.sms-bubble-inbound {
  background: #ffffff;
  border: 1px solid #e2e6ea;
  border-top-left-radius: 4px;
}

.sms-bubble-outbound {
  background: #dff1ff;
  border: 1px solid #b5dafd;
  border-top-right-radius: 4px;
}

.sms-bubble-failed {
  border-color: #dc3545;
  background: #fff5f6;
}

.sms-bubble-body {
  white-space: pre-wrap;
}

.manual-recipient-chip {
  font-size: 0.72rem;
  padding: 5px 9px;
  font-weight: 400;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  color: #495057;
}

.manual-recipient-chip--selected {
  border-color: #adb5bd;
  background: #f8f9fa;
}

.manual-recipient-check {
  font-size: 12px;
  color: #6c757d;
}

.sms-bubble-sender {
  margin-bottom: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #495057;
}

.sms-bubble-meta {
  margin-top: 4px;
  font-size: 0.74rem;
  color: #6c757d;
  text-align: right;
}

.sms-bubble-error {
  margin-top: 4px;
  font-size: 0.74rem;
  color: #dc3545;
}
</style>
